'use client';

import { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { findCurrentStepId } from '@/components/assistant-ui/thread/activity-trail-helpers';
import type { TrailIconRenderer } from '@/components/assistant-ui/thread/rotating-trail-icon';
import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { TrailRow } from '@/components/assistant-ui/thread/trail-row';
import { useClosingRowEntrance } from '@/components/assistant-ui/thread/use-closing-row-entrance';
import { useKeepContentBelowInPlace } from '@/components/assistant-ui/thread/use-keep-content-below-in-place';
import { useTrailCollapseMode } from '@/components/assistant-ui/thread/use-trail-collapse-mode';
import { ThinkingDot } from '@/components/ui/status-indicator';
import { MESSAGE_TEXT_SCALE } from '@/components/ui/type-scale';
import { cn } from '@/lib/utils';

export const TRAIL_PANEL_ATTR = 'data-trail-panel';

/** The fold's transition (`duration-300` on the panel) and a frame or two to spare. */
export const FOLD_SETTLE_MS = 300 + 100;

/**
 * React key for a row. `morphKey` (when set by `buildTrailTimeline`) claims
 * a stable `gap:N` slot — so a record that morphs into a trailing thinking
 * row's slot inherits the same key and React preserves the same `TrailRow`
 * instance, letting the icon + text crossfades inside fire for the morph.
 * Falls back to `row.id` for rows that don't claim a slot (the always-on
 * thinking header).
 */
export function rowReactKey(row: TimelineRow): string {
  return row.morphKey ?? row.id;
}

export function CollapsingTrail({
  rows: allRows,
  isLive,
  waitingOnGuest = false,
  renderInProgressIcon,
  renderStepTrailing,
  timerNode,
  expanded: controlledExpanded,
}: {
  rows: readonly TimelineRow[];
  isLive: boolean;
  /** The run is parked on the guest: not streaming, but not finished either, so it stays open. */
  waitingOnGuest?: boolean;
  renderInProgressIcon: TrailIconRenderer;
  renderStepTrailing?: (row: TimelineRow) => ReactNode | undefined;
  timerNode: ReactNode;
  // Forced expansion state. When provided, overrides internal toggle/auto-
  // collapse logic — useful for previews and demos that need a deterministic
  // open/closed state (e.g. Storybook frames).
  expanded?: boolean;
}) {
  const t = useTranslations();
  const panelId = useId();
  const trailIsLive = isLive || waitingOnGuest;
  // How many rows the trail had while it was last live: rows after these (the run's closing row)
  // arrive once it has stopped, and go straight below the panel as the summary.
  const [liveRowCount, setLiveRowCount] = useState(trailIsLive ? allRows.length : undefined);
  if (trailIsLive && liveRowCount !== allRows.length) setLiveRowCount(allRows.length);
  const {
    shownRows: rows,
    entering,
    dotRowIds,
  } = useClosingRowEntrance({
    rows: allRows,
    liveRowCount,
  });
  const dotRowKey = dotRowIds.join('\n');
  const renderRowIcon = useMemo<TrailIconRenderer>(() => {
    if (!dotRowKey) return renderInProgressIcon;
    const onDot = new Set(dotRowKey.split('\n'));
    return function renderEnteringRowIcon(row) {
      return onDot.has(row.stepId) ? <ThinkingDot /> : renderInProgressIcon(row);
    };
  }, [dotRowKey, renderInProgressIcon]);
  // The trail stays open until the closing row has made its entrance, so it never folds under it.
  const { renderPanelMode, expanded, toggle } = useTrailCollapseMode({
    isLive: trailIsLive || entering,
    controlledExpanded,
  });
  // Decided when the open trail appears, never while it shows: re-adding the class to a mounted
  // trail replays the fade, as a run resuming from a gate would.
  const [fadesIn, setFadesIn] = useState(isLive);
  const [openShown, setOpenShown] = useState(!renderPanelMode);
  if (openShown === renderPanelMode) {
    setOpenShown(!renderPanelMode);
    if (!renderPanelMode) setFadesIn(isLive);
  }
  // Opened on a run already parked (a thread switched to, a reload), the trail shows the state it
  // reached: only the steps the resumed run adds slide in.
  const [rowsAtParkedOpen] = useState(() =>
    !isLive && waitingOnGuest ? new Set(allRows.map(rowReactKey)) : undefined,
  );
  const trailRef = useRef<HTMLDivElement>(null);
  // The trail changes its own height from when it goes live until its fold has finished (a trail
  // that can't fold, once it stops); only meanwhile does it hold the conversation below it still.
  const [heightSettled, setHeightSettled] = useState(!trailIsLive);
  if (trailIsLive && heightSettled) setHeightSettled(false);
  if (!trailIsLive && renderPanelMode && rows.length <= 1 && !heightSettled) setHeightSettled(true);
  useKeepContentBelowInPlace({ ref: trailRef, active: rows.length > 0 && !heightSettled });
  const folding = !trailIsLive && renderPanelMode && rows.length > 1 && !expanded && !heightSettled;
  useEffect(
    function settleFoldWithoutTransition() {
      if (!folding) return;
      // With reduced motion the panel folds with no transition, so no transitionend settles it.
      const id = setTimeout(() => setHeightSettled(true), FOLD_SETTLE_MS);
      return () => clearTimeout(id);
    },
    [folding],
  );

  if (rows.length === 0) return null;

  const lastIdx = rows.length - 1;
  const currentRowId = findCurrentStepId(rows);
  const canToggle = renderPanelMode && rows.length > 1;
  const toggleLabel = expanded ? t('activityTrail.collapse') : t('activityTrail.expand');
  // The panel holds the rows the trail showed while live, and the summary below it the closing
  // row that arrives after; when the panel folds, the summary is what stays. Laid out this way from
  // the first step, finishing moves nothing: rebuilding or moving rows while the trail sat above
  // the screen made Safari scroll the conversation. Only a run that ends without a closing row moves
  // its last row down once the trail can fold.
  const summaryStart = canToggle
    ? lastIdx
    : trailIsLive
      ? rows.length
      : Math.min(liveRowCount ?? rows.length, rows.length);
  const panelRows = rows.slice(0, summaryStart);
  const summaryRows = rows.slice(summaryStart);

  const renderRow = (row: TimelineRow, opts: { showChevron: boolean }) => (
    <TrailRow
      key={rowReactKey(row)}
      row={row}
      expanded={expanded}
      isCurrent={row.id === currentRowId}
      showChevron={opts.showChevron}
      renderInProgressIcon={renderRowIcon}
      renderTrailing={renderStepTrailing}
      skipSlideIn={renderPanelMode || !!rowsAtParkedOpen?.has(rowReactKey(row))}
    />
  );

  return (
    <div
      ref={trailRef}
      style={MESSAGE_TEXT_SCALE}
      className={cn(
        'aui-progress-tracker relative -mx-3 my-2 w-[calc(100%+1.5rem)] rounded-md transition-colors select-none',
        canToggle && 'hover:bg-muted/40',
        !renderPanelMode &&
          fadesIn &&
          'motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in',
      )}
    >
      {rows.length > 1 && (
        // Laid over the whole trail rather than wrapping it: a button's content is presentational,
        // so wrapped, the steps would never reach a screen reader.
        <button
          type="button"
          hidden={!canToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={toggleLabel}
          onClick={toggle}
          className="absolute inset-0 z-10 cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      {/* CSS grid `1fr → 0fr` collapsible: pure CSS transition, no JS in the animation loop.
          Opacity animates alongside the height so rows fade out as they shrink instead of
          guillotining at full alpha. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          !canToggle || expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
        id={panelId}
        onTransitionEnd={function settleOnceFolded(event) {
          if (event.target === event.currentTarget && canToggle && !expanded) {
            setHeightSettled(true);
          }
        }}
        aria-hidden={canToggle ? !expanded : undefined}
        {...(canToggle && { [TRAIL_PANEL_ATTR]: '' })}
      >
        <div className={cn('min-h-0', canToggle && 'overflow-hidden')}>
          <ol className="flex flex-col">
            {panelRows.map(row => renderRow(row, { showChevron: false }))}
          </ol>
        </div>
      </div>
      <ol className="flex flex-col">
        {summaryRows.map(row => renderRow(row, { showChevron: canToggle }))}
      </ol>
      {timerNode}
    </div>
  );
}
